import { gameState } from '../state/gameState.js';
import { messageFor } from '../api/errors.js';
import { showToast } from './Toast.js';
import { showModal } from './Modal.js';
import { showDressingModal } from './DressingModal.js';
import { placeMode } from './PlaceMode.js';
import { lockInput, unlockInput } from './inputLock.js';
import { isFlippable, GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { FURNITURE_SPOTS, isSeatFurniture } from './furnitureSpots.mjs';
import { placementSale, saleNote } from '../state/resale.mjs';
import { STAND_LABEL, TAKEN_TOAST } from './FurnitureSeats.js';

const SEAT_MENU_LABEL = { sit: 'Usiądź {sofa}', lie: 'Połóż się {bed}' };
import { audioManager } from '../audio/AudioManager.js';
import { isClock, liveClockTime } from '../ui/clockHand.js';
import { formatClock } from '../state/gameClock.js';
import { iconText } from './iconText.js';
import { enlargeForTouch } from './touchEnlarge.js';
import { menuCentre } from './menuPlacement.mjs';

// Context menu for a placed object. Opened by tapping the item (see placementInteraction.js)
// or right-clicking it. Buttons are built dynamically — a wardrobe (szafa) also gets "Garderoba",
// a clock (zegar) a live, non-clickable "🕑 HH:MM · pora" readout of the in-game time.
export function showContextMenu(scene, x, y, placement) {
  const layer = scene.add.container(0, 0).setDepth(2500);
  lockInput(scene);

  // World units: the layer scrolls with the scene, so the overlay covers what the camera shows now (the
  // wide Podwórko scrolls), not the world's first screen.
  const cam = scene.cameras.main;
  const ox = cam.scrollX, oy = cam.scrollY;
  const overlay = scene.add.rectangle(ox, oy, GAME_WIDTH, GAME_HEIGHT, 0, 0)
    .setOrigin(0, 0).setInteractive();
  layer.add(overlay);

  const liveTexts = [];
  const tick = () => { for (const { txt, text } of liveTexts) txt.setText(text()); };
  scene.events.on('update', tick);
  const close = () => { scene.events.off('update', tick); unlockInput(scene); layer.destroy(); };
  overlay.on('pointerup', close);

  const isWardrobe = placement.type === 'furniture' && placement.item === 'szafa';

  const actions = [];
  if (isClock(placement)) {
    actions.push({ text: () => `{clock} ${formatClock(liveClockTime(), { stepMinutes: 15 })}`, fill: 0xf3e9d2, color: '#3b2a1a' });
  }
  if (isWardrobe) {
    actions.push({ label: '{dress} Garderoba', fill: 0xffd166, color: '#000', fn: () => { close(); showDressingModal(scene); } });
  }
  // Sofas and beds: sit / lie on the free spot nearest the tap (x, y), or stand up from this piece (FurnitureSeats).
  const seats = scene.furnitureSeats;
  if (seats && isSeatFurniture(placement)) {
    const pose = FURNITURE_SPOTS[placement.item].pose;
    if (seats.isOn(placement.id)) {
      actions.push({ label: STAND_LABEL, fill: 0x90e0a0, color: '#000', fn: () => { close(); seats.stand(); } });
    } else if (seats.freeSpotsOn(placement.id) > 0) {
      actions.push({
        label: SEAT_MENU_LABEL[pose], fill: 0x90e0a0, color: '#000',
        fn: () => { close(); if (!seats.sitOnPiece(placement.id, { x, y })) showToast(scene, TAKEN_TOAST); },
      });
    }
  }
  if (isFlippable(placement)) {
    actions.push({
      label: 'Odwróć {arrows_lr}', fill: 0xa8d8ff, color: '#000',
      fn: async () => {
        close();
        try {
          await gameState.toggleFlip(placement.id);
        } catch (e) {
          showToast(scene, messageFor(e));
        }
      },
    });
  }
  actions.push({ label: 'Przesuń', fill: 0xffd166, color: '#000', fn: () => {
    close();
    placeMode.enterMove(scene, placement.id, placement.type, placement.item, !!placement.flipped);
  }});
  // Back into the Schowek, to be placed again (the old "delete" label read as selling it, owner 2026-10-30). A buda's animal goes.
  actions.push({ label: 'Schowaj', fill: 0xef4444, color: '#fff', fn: async () => {
    close();
    let body = 'Trafi do Schowka.';
    if (placement.type === 'buda') body += '\nUWAGA: Zwierzak zniknie!';
    const ok = await showModal(scene, { title: 'Schować?', body, confirmLabel: 'Tak, schowaj', cancelLabel: 'Nie' });
    if (!ok) return;
    try {
      await gameState.removeObject(placement.id);
      showToast(scene, 'W Schowku');
    } catch (e) {
      showToast(scene, messageFor(e));
    }
  }});
  // Furniture and decorations sell back for 80 % (of the old price if bought before 2026-10-20; owner 2026-10-30).
  const sale = placementSale(gameState.raw, placement);
  if (sale) {
    actions.push({ label: `Sprzedaj za ${sale.coins} {coin}`, fill: 0xf4a261, color: '#000', fn: async () => {
      close();
      const ok = await showModal(scene, { title: `Sprzedać za ${sale.coins} {coin}?`, body: saleNote(sale), confirmLabel: 'Sprzedaj', cancelLabel: 'Nie' });
      if (!ok) return;
      try {
        await gameState.sellObject(placement.id);
        showToast(scene, `Sprzedane: +${sale.coins} {coin}`);
      } catch (e) {
        showToast(scene, messageFor(e));
      }
    }});
  }

  // Vertical stack, clamped to stay on screen.
  const BTN_W = 170, BTN_H = 40, GAP = 8, PAD = 12;
  const menuW = BTN_W + PAD * 2;
  const menuH = actions.length * BTN_H + (actions.length - 1) * GAP + PAD * 2;
  // World units; scene.scale.* is the retina canvas size (2x). Clamped to the camera's view.
  const { x: cx, y: cy } = menuCentre(x, y, menuW, menuH, { ox, oy, vw: GAME_WIDTH, vh: GAME_HEIGHT });

  const bg = scene.add.rectangle(cx, cy, menuW, menuH, 0xffffff, 0.97).setStrokeStyle(2, 0x333333);
  layer.add(bg);

  const firstY = cy - menuH / 2 + PAD + BTN_H / 2;
  actions.forEach((act, i) => {
    const by = firstY + i * (BTN_H + GAP);
    const btn = scene.add.rectangle(cx, by, BTN_W, BTN_H, act.fill).setStrokeStyle(2, 0x333333);
    const txt = iconText(scene, cx, by, act.text ? act.text() : act.label, {
      fontFamily: 'system-ui', fontSize: '15px', color: act.color, fontStyle: 'bold',
    }).setOrigin(0.5);
    if (act.text) {
      liveTexts.push({ txt, text: act.text });   // read-only label, refreshed every frame
    } else {
      btn.setInteractive({ useHandCursor: true });
      btn.on('pointerup', () => { audioManager.playSfx('click'); act.fn(); });
    }
    layer.add([btn, txt]);
  });
  enlargeForTouch(scene, layer, { x: cx, y: cy, w: menuW, h: menuH }, { overlay });   // finger-sized on phones
}
